import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { StorySubmissionForm } from '@/components/stories/StorySubmissionForm';
import { UserIcon, UsersIcon, HeartIcon, BriefcaseIcon, PlusIcon, SparklesIcon } from '@heroicons/react/24/outline';
import SEO from '@/components/SEO';
import { OG_IMAGES } from '@/constants/seoImages';
import { ImageOptimizer } from '@/components/seo/ImageOptimizer';

const Stories = () => {
  const [isSubmissionOpen, setIsSubmissionOpen] = useState(false);

  const handleSubmissionSuccess = () => {
    setIsSubmissionOpen(false);
  };

  return (
    <>
      <SEO
        title="Stories of Thriving at Home - SeniorThrive"
        description="Share your story of thriving at home. We are building a community of older adults, families, caregivers, and professionals who believe independence at home is worth fighting for."
        keywords="senior stories, caregiving experiences, aging at home, family caregivers, thriving at home, aging in place stories"
        canonical="https://seniorthrive.com/stories"
        image={OG_IMAGES.stories}
      />

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <ImageOptimizer
              src="/uploads/hero-family-watercolor.jpg"
              alt="Older adult thriving independently at home"
              className=""
              imgClassName="w-full h-full object-cover"
              sizes="100vw"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/80 to-background"></div>
          </div>

          <div className="relative max-w-4xl mx-auto text-center z-10">
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              Stories of <span className="text-primary">Thriving</span>
            </h1>

            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
              SeniorThrive is built on the belief that everyone deserves to live safely and
              fully at home. As our community grows, this is where their stories will live.
            </p>

            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
              Are you part of our early access community? We would love to hear about your experience.
            </p>

            <Dialog open={isSubmissionOpen} onOpenChange={setIsSubmissionOpen}>
              <DialogTrigger asChild>
                <Button size="lg" className="inline-flex items-center gap-2 text-lg px-8 py-6">
                  <PlusIcon className="w-5 h-5" />
                  Share Your Story
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <StorySubmissionForm onSuccess={handleSubmissionSuccess} />
              </DialogContent>
            </Dialog>
          </div>
        </section>

        {/* What We Want to Hear */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Stories We Are Looking For
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Every journey is different. Here are some of the perspectives that help others.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  icon: UserIcon,
                  title: 'Older Adults',
                  prompt: 'How has staying at home shaped your daily life? What gives you confidence? What tools or habits help you feel independent?',
                  color: 'primary',
                },
                {
                  icon: UsersIcon,
                  title: 'Family Members',
                  prompt: 'How do you balance supporting a parent with respecting their independence? What changed when you found a system that worked?',
                  color: 'emerald',
                },
                {
                  icon: HeartIcon,
                  title: 'Caregivers',
                  prompt: 'What does good communication with a family look like? How do the right tools change the quality of care you can provide?',
                  color: 'amber',
                },
                {
                  icon: BriefcaseIcon,
                  title: 'Professionals',
                  prompt: 'How has technology changed how you serve aging clients? What do families need most from professionals like you?',
                  color: 'primary',
                },
              ].map((item, i) => {
                const colors = {
                  primary: { bg: 'bg-primary/10', icon: 'text-primary', border: 'border-primary/15 hover:border-primary/30' },
                  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200/50 hover:border-emerald-400/50' },
                  amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', icon: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200/50 hover:border-amber-400/50' },
                };
                const style = colors[item.color as keyof typeof colors];
                return (
                  <div key={i} className={`p-8 rounded-md bg-card border ${style.border} transition-all duration-300 hover:shadow-lg`}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-12 h-12 ${style.bg} rounded-xl flex items-center justify-center`}>
                        <item.icon className={`w-6 h-6 ${style.icon}`} />
                      </div>
                      <h3 className="text-xl font-bold text-foreground">{item.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed italic">
                      "{item.prompt}"
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Coming Soon Placeholder */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 rounded-md flex items-center justify-center mx-auto mb-6">
              <SparklesIcon className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Community Stories Coming Soon
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              As our early access members begin their journeys with SeniorThrive,
              their stories will appear here, real experiences from real people,
              in their own words.
            </p>
            <p className="text-muted-foreground">
              Want to be one of the first?
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-primary/5 to-accent/10 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Your Story Matters
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Whether you are an older adult thriving at home, a family member finding balance,
              or a professional making a difference, your experience could inspire someone else.
            </p>

            <Dialog open={isSubmissionOpen} onOpenChange={setIsSubmissionOpen}>
              <DialogTrigger asChild>
                <Button size="lg" className="inline-flex items-center gap-2 text-lg px-8 py-6">
                  <PlusIcon className="w-5 h-5" />
                  Share Your Story
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <StorySubmissionForm onSuccess={handleSubmissionSuccess} />
              </DialogContent>
            </Dialog>
          </div>
        </section>
      </div>
    </>
  );
};

export default Stories;
